import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  X, 
  Bot, 
  ShieldCheck, 
  Sparkles, 
  CheckCircle, 
  AlertTriangle, 
  UploadCloud, 
  FileText, 
  Cpu, 
  Fingerprint
} from 'lucide-react';
import useLang from '../hooks/useLang';

const STORAGE_HIDDEN_KEY = 'adca_ai_sentinel_widget_hidden';

const translations = {
  es: {
    widgetTitle: 'AI Sentinel',
    widgetSubtitle: 'Transparencia de IA · EU AI Act',
    status: 'Art. 50 Conforme',
    hideTooltip: 'Ocultar AI Sentinel',
    tabDeclaration: 'Declaración IA',
    tabChecker: 'Verificador',
    tabBadge: 'Sello Digital',
    
    declTitle: 'Sistemas de IA Declarados',
    declDesc: 'Registro público de sistemas de IA y modelos generativos activos en este dominio, conforme al Reglamento de IA de la UE.',
    systemChatbot: 'Asistente de Soporte (NLP)',
    systemChatbotDesc: 'IA interactiva para orientación legal y soporte técnico.',
    systemBlog: 'Generación de Contenidos (Generativa)',
    systemBlogDesc: 'Redacción asistida por LLMs y traducción de artículos.',
    systemImages: 'Material Gráfico (Diseño)',
    systemImagesDesc: 'Imágenes vectoriales optimizadas y mockups conceptuales.',
    statusActive: 'Activo y Declarado',

    checkTitle: 'Analizador de Metadatos de IA',
    checkDesc: 'Arrastra un archivo o simula un análisis para verificar la presencia de marcas de agua o metadatos de IA generativa (SynthID, C2PA, IPTC).',
    testImages: 'O selecciona una de nuestras imágenes de muestra:',
    sampleAi: 'Imagen IA (Cerebro)',
    sampleHuman: 'Imagen Humana (Oficina)',
    btnSimulate: 'Simular Análisis OSINT',
    checking: 'Escaneando firmas de IA...',
    checkSuccess: '¡ANÁLISIS COMPLETADO!',
    checkDetected: 'IA Generativa Detectada (DALL-E 3 / Midjourney v6). Requiere etiqueta de transparencia.',
    checkNotDetected: 'Sin firmas de IA detectadas. Contenido de origen humano.',
    
    badgeTitle: 'Certificación de Transparencia',
    badgeDesc: 'Este dominio cuenta con la auditoría de transparencia de IA emitida por Auditoría Legal, garantizando el uso ético y el cumplimiento normativo.',
    badgeHash: 'HASH DE INTEGRIDAD DE IA',
    footerInfo: 'AI Sentinel v1.0 · Auditorialegal.es',
    viewFullPolicy: 'Ver Política de Gobernanza de IA Completa',
  },
  en: {
    widgetTitle: 'AI Sentinel',
    widgetSubtitle: 'AI Transparency · EU AI Act',
    status: 'Art. 50 Compliant',
    hideTooltip: 'Hide AI Sentinel',
    tabDeclaration: 'AI Registry',
    tabChecker: 'AI Analyzer',
    tabBadge: 'Digital Seal',
    
    declTitle: 'Declared AI Systems',
    declDesc: 'Public registry of active AI systems and generative models on this domain, compliant with the EU AI Act.',
    systemChatbot: 'Support Assistant (NLP)',
    systemChatbotDesc: 'Interactive AI for legal guidance and technical support.',
    systemBlog: 'Content Generation (Generative)',
    systemBlogDesc: 'Assisted drafting by LLMs and article translations.',
    systemImages: 'Graphic Material (Design)',
    systemImagesDesc: 'Optimized vector graphics and conceptual mockups.',
    statusActive: 'Active & Declared',

    checkTitle: 'AI Metadata Analyzer',
    checkDesc: 'Drag a file or simulate an analysis to verify the presence of watermarks or generative AI metadata (SynthID, C2PA, IPTC).',
    testImages: 'Or select one of our sample images:',
    sampleAi: 'AI Image (Brain)',
    sampleHuman: 'Human Image (Office)',
    btnSimulate: 'Simulate OSINT Scan',
    checking: 'Scanning for AI signatures...',
    checkSuccess: 'SCAN COMPLETED!',
    checkDetected: 'Generative AI Detected (DALL-E 3 / Midjourney v6). Transparency tag required.',
    checkNotDetected: 'No AI signatures detected. Human-created content.',

    badgeTitle: 'Transparency Certification',
    badgeDesc: 'This domain has been audited for AI transparency by Auditorialegal, ensuring ethical usage and regulatory compliance.',
    badgeHash: 'AI INTEGRITY HASH',
    footerInfo: 'AI Sentinel v1.0 · Auditorialegal.es',
    viewFullPolicy: 'View Full AI Governance Policy',
  }
};

export const AISentinelWidget = () => {
  const lang = useLang();
  const t = translations[lang] || translations.es;

  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const [activeTab, setActiveTab] = useState('declaration');

  // Analyzer simulator states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null); // 'detected' | 'clean'

  useEffect(() => {
    const isHidden = localStorage.getItem(STORAGE_HIDDEN_KEY);
    if (isHidden === '1') {
      setVisible(false);
    }
  }, []);

  const handleHideWidget = (e) => {
    e.stopPropagation();
    localStorage.setItem(STORAGE_HIDDEN_KEY, '1');
    setVisible(false);
    setOpen(false);
  };

  const handleSimulateScan = () => {
    setIsAnalyzing(true);
    setAnalysisResult(null);
    setTimeout(() => {
      setIsAnalyzing(false);
      // Randomly simulate AI detection or clean content
      setAnalysisResult(Math.random() > 0.4 ? 'detected' : 'clean');
    }, 2000);
  };

  const handleImageScan = (type) => {
    setIsAnalyzing(true);
    setAnalysisResult(null);
    setTimeout(() => {
      setIsAnalyzing(false);
      setAnalysisResult(type);
    }, 2000);
  };

  if (!visible) return null;

  return (
    <>
      {/* Botón Flotante Global (Ubicado a la izquierda de la Ventanilla de Cumplimiento) */}
      <div 
        className="fixed z-[9997] group font-sans"
        style={{ bottom: '16px', right: '72px' }}
      >
        {/* Botón de cerrar (X) */}
        <button
          onClick={handleHideWidget}
          title={t.hideTooltip}
          aria-label={t.hideTooltip}
          className="absolute -top-1 -left-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 w-4 h-4 text-[9px] flex items-center justify-center border border-white/20 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-[10000]"
        >
          <X className="w-2.5 h-2.5" />
        </button>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="relative flex items-center justify-center w-10 h-10 rounded-full shadow-[0_8px_24px_rgba(124,58,237,0.45)] border-2 border-violet-500/40 bg-gradient-to-br from-[#0c0d21] to-[#1e1b4b] hover:scale-105 active:scale-95 transition-all duration-300"
        >
          <span className="absolute inset-0 rounded-full border border-violet-500/20 animate-ping opacity-30"></span>
          <Brain className="w-5 h-5 text-violet-400 group-hover:rotate-12 transition-transform" />
          
          {/* Tooltip flotante */}
          <span className="absolute right-14 bg-slate-950 text-violet-400 text-[10px] font-bold px-2.5 py-1 rounded-lg border border-slate-800 opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity shadow-xl pointer-events-none">
            {t.widgetTitle}
          </span>
        </button>
      </div>

      {/* Panel Desplegado */}
      {open && (
        <div
          className="fixed bottom-[calc(80px+env(safe-area-inset-bottom,0px))] right-4 sm:right-16 z-[9997] w-[min(410px,calc(100vw-2rem))] h-[min(580px,calc(100vh-10rem))] rounded-3xl overflow-hidden shadow-2xl border border-slate-800/80 bg-[#0b0f19] flex flex-col font-sans"
          role="dialog"
          aria-label="AI Sentinel - Transparencia de IA"
        >
          {/* Cabecera Premium */}
          <div className="flex items-center justify-between px-4 py-3.5 bg-gradient-to-r from-slate-950 to-slate-900 border-b border-slate-800/60 text-white">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
                <Bot className="w-4.5 h-4.5 text-violet-400" />
              </div>
              <div className="leading-tight">
                <p className="font-semibold text-xs text-white">{t.widgetTitle}</p>
                <p className="text-[10px] text-violet-400 font-mono flex items-center gap-1 font-bold">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                  {t.status}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menú de Pestañas */}
          <div className="bg-slate-950 px-2 flex border-b border-slate-900/60 overflow-x-auto text-[10px] scrollbar-none shrink-0">
            <button
              onClick={() => setActiveTab('declaration')}
              className={`px-3 py-2.5 border-b-2 font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                activeTab === 'declaration' ? 'border-violet-500 text-violet-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <FileText className="w-3.5 h-3.5" /> {t.tabDeclaration}
            </button>
            <button
              onClick={() => setActiveTab('checker')}
              className={`px-3 py-2.5 border-b-2 font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                activeTab === 'checker' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <Cpu className="w-3.5 h-3.5" /> {t.tabChecker}
            </button>
            <button
              onClick={() => setActiveTab('badge')}
              className={`px-3 py-2.5 border-b-2 font-bold flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                activeTab === 'badge' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <Fingerprint className="w-3.5 h-3.5" /> {t.tabBadge}
            </button>
          </div>

          {/* Contenido Scrollable */}
          <div className="flex-1 overflow-y-auto p-4 bg-[#080b14] text-white">
            
            {/* PESTAÑA: DECLARACIÓN IA */}
            {activeTab === 'declaration' && (
              <div className="space-y-4">
                <div className="bg-slate-900/60 border border-slate-800/80 p-3.5 rounded-xl text-[11px] leading-relaxed text-slate-300 text-left">
                  <p className="text-violet-400 font-bold mb-1">{t.declTitle}</p>
                  {t.declDesc}
                </div>

                <div className="space-y-2.5 text-left">
                  <div className="p-3 bg-slate-950/80 border border-slate-900 rounded-xl flex justify-between items-start gap-3">
                    <div>
                      <p className="text-xs font-bold text-slate-200">{t.systemChatbot}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5 leading-normal">{t.systemChatbotDesc}</p>
                    </div>
                    <span className="text-[9px] bg-violet-500/10 text-violet-400 border border-violet-500/20 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 font-semibold">
                      {t.statusActive}
                    </span>
                  </div>

                  <div className="p-3 bg-slate-950/80 border border-slate-900 rounded-xl flex justify-between items-start gap-3">
                    <div>
                      <p className="text-xs font-bold text-slate-200">{t.systemBlog}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5 leading-normal">{t.systemBlogDesc}</p>
                    </div>
                    <span className="text-[9px] bg-violet-500/10 text-violet-400 border border-violet-500/20 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 font-semibold">
                      {t.statusActive}
                    </span>
                  </div>

                  <div className="p-3 bg-slate-950/80 border border-slate-900 rounded-xl flex justify-between items-start gap-3">
                    <div>
                      <p className="text-xs font-bold text-slate-200">{t.systemImages}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5 leading-normal">{t.systemImagesDesc}</p>
                    </div>
                    <span className="text-[9px] bg-violet-500/10 text-violet-400 border border-violet-500/20 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 font-semibold">
                      {t.statusActive}
                    </span>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-900 flex justify-center">
                  <a
                    href="/politica-ia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[9px] text-violet-400 hover:text-violet-300 underline font-semibold flex items-center gap-1 transition-colors"
                  >
                    {t.viewFullPolicy}
                  </a>
                </div>
              </div>
            )}

            {/* PESTAÑA: ANALIZADOR DE IA */}
            {activeTab === 'checker' && (
              <div className="space-y-4">
                <div className="bg-slate-900/60 border border-slate-800/80 p-3.5 rounded-xl text-[11px] leading-relaxed text-slate-300 text-left">
                  <p className="text-cyan-400 font-bold mb-1">{t.checkTitle}</p>
                  {t.checkDesc}
                </div>

                <div className="border border-dashed border-slate-800 bg-slate-950/40 rounded-2xl p-6 text-center flex flex-col items-center justify-center gap-3 transition-colors hover:border-cyan-500/40">
                  <UploadCloud className="w-10 h-10 text-slate-500" />
                  <p className="text-xs text-slate-400">Suelta tu imagen o documento aquí para escanear firmas IPTC o marcas de agua invisibles</p>
                </div>

                {!isAnalyzing && analysisResult === null && (
                  <div className="space-y-3">
                    <button
                      onClick={handleSimulateScan}
                      className="w-full py-2.5 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 text-white font-bold rounded-xl text-xs transition shadow-md"
                    >
                      {t.btnSimulate}
                    </button>

                    <div className="space-y-2 border-t border-slate-900 pt-3 text-left">
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{t.testImages}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleImageScan('detected')}
                          className="p-1.5 bg-slate-950 hover:bg-slate-900 border border-slate-900 hover:border-violet-500/40 rounded-xl transition text-left flex items-center gap-2 group"
                        >
                          <img src="/images/cyber_brain_ai.png" alt="AI Brain" className="w-10 h-10 object-cover rounded-lg border border-slate-800" />
                          <div className="leading-tight">
                            <p className="text-[9px] font-bold text-slate-200 group-hover:text-violet-400 transition-colors">{t.sampleAi}</p>
                            <p className="text-[8px] text-violet-400/80 font-mono">DALL-E 3</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleImageScan('clean')}
                          className="p-1.5 bg-slate-950 hover:bg-slate-900 border border-slate-900 hover:border-emerald-500/40 rounded-xl transition text-left flex items-center gap-2 group"
                        >
                          <img src="/images/human_office_photo.png" alt="Human Office" className="w-10 h-10 object-cover rounded-lg border border-slate-800" />
                          <div className="leading-tight">
                            <p className="text-[9px] font-bold text-slate-200 group-hover:text-emerald-400 transition-colors">{t.sampleHuman}</p>
                            <p className="text-[8px] text-emerald-400/80 font-mono">Foto Real</p>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {isAnalyzing && (
                  <div className="bg-slate-950 border border-slate-900 p-4 rounded-xl flex flex-col items-center justify-center gap-3 py-6">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-cyan-500"></span>
                    </span>
                    <p className="text-[10px] font-mono text-cyan-400 animate-pulse">{t.checking}</p>
                  </div>
                )}

                {!isAnalyzing && analysisResult !== null && (
                  <div className={`p-4 rounded-xl border font-mono text-[9px] space-y-2 text-left ${
                    analysisResult === 'detected' 
                      ? 'bg-rose-500/10 border-rose-500/30 text-rose-400' 
                      : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                  }`}>
                    <p className="font-bold font-sans text-xs flex items-center gap-1.5">
                      {analysisResult === 'detected' ? (
                        <>
                          <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                          {t.checkSuccess}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                          {t.checkSuccess}
                        </>
                      )}
                    </p>
                    <p className="text-slate-300 leading-normal">
                      {analysisResult === 'detected' ? t.checkDetected : t.checkNotDetected}
                    </p>
                    <button
                      onClick={() => setAnalysisResult(null)}
                      className="text-[9px] underline text-slate-400 hover:text-white pt-1 block"
                    >
                      Analizar otro archivo
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* PESTAÑA: SELLO DIGITAL */}
            {activeTab === 'badge' && (
              <div className="space-y-4 text-center">
                <div className="bg-slate-900/60 border border-slate-800/80 p-3.5 rounded-xl text-left text-[11px] leading-relaxed text-slate-300">
                  <p className="text-emerald-400 font-bold mb-1">{t.badgeTitle}</p>
                  {t.badgeDesc}
                </div>

                <div className="py-6 flex flex-col items-center justify-center">
                  <div className="relative w-28 h-28 bg-gradient-to-br from-slate-950 to-slate-900 border border-emerald-500/30 rounded-2xl flex flex-col items-center justify-center p-3 shadow-lg group">
                    {/* Ring animation */}
                    <div className="absolute inset-0.5 border border-emerald-500/10 rounded-2xl group-hover:scale-105 transition-transform" />
                    
                    <ShieldCheck className="w-12 h-12 text-emerald-400 animate-pulse" />
                    <span className="text-[8px] font-black text-slate-300 tracking-wider uppercase mt-2">IA TRANSPARENTE</span>
                    <span className="text-[6.5px] font-mono text-emerald-400 mt-0.5">AUDITORIALEGAL</span>
                  </div>
                </div>

                <div className="bg-slate-950 border border-slate-900 p-3 rounded-xl text-left font-mono text-[9px] text-emerald-400 space-y-1">
                  <p className="text-[8px] text-slate-500 font-bold uppercase">{t.badgeHash}</p>
                  <p className="text-white select-all break-all cursor-pointer font-bold bg-slate-900 p-2 rounded border border-slate-800 leading-tight">
                    AI_ACT_SHA256: d8f3c7a0e1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8
                  </p>
                </div>
              </div>
            )}

          </div>

          {/* Pie de Panel */}
          <div className="p-3 bg-slate-950 border-t border-slate-900 flex justify-between items-center text-[8.5px] text-slate-500 font-mono shrink-0">
            <span>{t.footerInfo}</span>
            <span className="text-violet-400 font-bold flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-violet-400" /> EU AI ACT CONFORME
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default AISentinelWidget;
